import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { TbRefresh } from "react-icons/tb";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasPlatformCreateAccess } from "../../../../Hooks/useBaasPlatformCreateAccess";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useToast } from "../../../../Hooks/toast";
import {
  fetchBaasInboundWebhookLogDetail,
  fetchBaasInboundWebhookLogs,
  fetchBaasPartnerHttpLogs,
  fetchBaasProviderPaySmartConfig,
  fetchBaasProviderPixLimit,
  updateBaasProviderPixOutboundRules,
  updateBaasProviderPixSpiLimit,
} from "../../../../Services/baasProviderConsoleApi";
import { BaasTablePageSkeleton } from "../components/BaasSkeleton";
import { GridPagination } from "../../../../Components/Molecules/GridHistory";

const TABS = [
  { id: "config", label: "Configuração PaySmart" },
  { id: "webhooks", label: "Webhooks inbound" },
  { id: "http", label: "Logs HTTP parceiro" },
];

const formatDate = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "medium" }).format(
    new Date(value),
  );
};

const ConfigPanel = ({
  config,
  pixLimit,
  loading,
  spiDraft,
  onSpiDraftChange,
  onSaveSpi,
  spiSaving,
  ruleDrafts,
  onRuleDraftChange,
  onSaveRules,
  rulesSaving,
}) => {
  if (loading) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">Carregando configuração…</p>
    );
  }

  const rows = config
    ? [
        { label: "Ambiente", value: config.environment },
        { label: "Mock", value: config.mock_enabled ?? config.mockEnabled ? "Sim" : "Não" },
        {
          label: "Credenciais",
          value: config.credentials_environment ?? config.credentialsEnvironment,
        },
        { label: "Shards Pix", value: config.shard_count ?? config.shardCount },
        {
          label: "Timeout Rabbit (s)",
          value: config.rabbit_timeout_seconds ?? config.rabbitTimeoutSeconds,
        },
      ]
    : [];

  const outboundRules = pixLimit?.outbound_rules ?? pixLimit?.outboundRules ?? [];

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Limite provedor usado apenas para <strong>split de Pix saída</strong> em lote. Limites de
        entrada e per operation do cliente ficam em Limites da instância/conta.
      </p>

      {rows.length ? (
        <div className="rounded-lg border border-gray-200 dark:border-brand-dark-border divide-y divide-gray-100 dark:divide-brand-dark-border">
          {rows.map((row) => (
            <div
              key={row.label}
              className="flex flex-col gap-0.5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <span className="text-xs text-gray-500 dark:text-gray-400">{row.label}</span>
              <span className="text-sm font-medium text-brand-dark dark:text-gray-100">
                {row.value ?? "—"}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">Configuração indisponível.</p>
      )}

      <div className="rounded-lg border border-gray-200 p-4 dark:border-brand-dark-border">
        <h2 className="text-sm font-semibold text-brand-dark dark:text-gray-100">
          Limite simples provedor (SPI)
        </h2>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Teto máximo por Pix no SPI PaySmart (major BRL).
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-gray-500">
            SPI max (R$)
            <input
              type="text"
              className="rounded border border-gray-200 px-3 py-2 text-sm dark:border-brand-dark-border dark:bg-brand-dark-elevated"
              value={spiDraft}
              onChange={(e) => onSpiDraftChange(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="btn-primary w-auto"
            disabled={spiSaving}
            onClick={onSaveSpi}
          >
            {spiSaving ? "Salvando…" : "Salvar SPI"}
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-brand-dark-border overflow-hidden">
        <div className="border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border">
          <h2 className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            Pix saída — janelas (split lote)
          </h2>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Dia 6h–20h · 20h–22h · após 22h–6h (America/Sao_Paulo).
          </p>
        </div>
        {!outboundRules.length ? (
          <p className="px-4 py-6 text-sm text-gray-500">Sem regras configuradas.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 dark:bg-brand-dark-elevated">
              <tr>
                <th className="px-4 py-2">Tipo</th>
                <th className="px-4 py-2">Janela</th>
                <th className="px-4 py-2">Per operation (R$)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-brand-dark-border">
              {outboundRules.map((rule) => {
                const key = `${rule.limit_type ?? rule.limitType}-${rule.period}`;
                return (
                  <tr key={rule.id ?? key}>
                    <td className="px-4 py-2">{rule.label ?? rule.type_code ?? rule.typeCode}</td>
                    <td className="px-4 py-2">
                      {rule.period_hours ?? rule.periodHours ?? rule.period_label ?? rule.periodLabel}
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        className="w-28 rounded border border-gray-200 px-2 py-1 text-sm dark:border-brand-dark-border dark:bg-brand-dark-elevated"
                        value={ruleDrafts[key] ?? ""}
                        onChange={(e) => onRuleDraftChange(key, e.target.value)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="border-t border-gray-100 px-4 py-3 dark:border-brand-dark-border">
          <button
            type="button"
            className="btn-outline w-auto"
            disabled={rulesSaving || !outboundRules.length}
            onClick={onSaveRules}
          >
            {rulesSaving ? "Salvando…" : "Salvar janelas saída"}
          </button>
        </div>
      </div>
    </div>
  );
};

const WebhooksPanel = ({
  items,
  loading,
  total,
  page,
  pageSize,
  onPageChange,
  onSelect,
  selectedId,
  selectedDetail,
  detailLoading,
}) => (
  <div className="grid gap-4 lg:grid-cols-2">
    <div className="min-w-0 overflow-hidden rounded-lg border border-gray-200 dark:border-brand-dark-border">
      {loading ? (
        <p className="px-4 py-8 text-center text-sm text-gray-500">Carregando…</p>
      ) : !items.length ? (
        <p className="px-4 py-8 text-center text-sm text-gray-500">Sem webhooks inbound.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-brand-dark-border">
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className={`w-full px-4 py-3 text-left transition hover:bg-gray-50 dark:hover:bg-brand-dark-elevated/40 ${
                  selectedId === item.id ? "bg-[#e7a73f]/10" : ""
                }`}
                onClick={() => onSelect(item.id)}
              >
                <p className="text-xs text-gray-400">{formatDate(item.occurred_at ?? item.occurredAt)}</p>
                <p className="mt-1 truncate text-sm font-medium text-brand-dark dark:text-gray-100">
                  {item.body_preview ?? item.bodyPreview ?? "Webhook"}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="border-t border-gray-100 px-2 py-2 dark:border-brand-dark-border">
        <GridPagination
          total={total}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={onPageChange}
          onRowsPerPageChange={() => {}}
          currentPageCount={items.length}
          itemLabel="eventos"
        />
      </div>
    </div>
    <div className="min-w-0 rounded-lg border border-gray-200 p-4 dark:border-brand-dark-border">
      <h3 className="text-sm font-bold text-brand-dark dark:text-gray-100">Corpo do evento</h3>
      {detailLoading ? (
        <p className="mt-4 text-sm text-gray-500">Carregando…</p>
      ) : !selectedDetail ? (
        <p className="mt-4 text-sm text-gray-500">Selecione um evento na lista.</p>
      ) : (
        <pre className="mt-4 max-h-[420px] overflow-auto rounded-lg bg-gray-50 p-3 text-xs text-gray-700 dark:bg-brand-dark-elevated dark:text-gray-200">
          {selectedDetail.body ?? selectedDetail.body_preview ?? selectedDetail.bodyPreview ?? "—"}
        </pre>
      )}
    </div>
  </div>
);

const HttpLogsPanel = ({ items, loading, total, page, pageSize, onPageChange }) => (
  <div className="min-w-0 overflow-hidden rounded-lg border border-gray-200 dark:border-brand-dark-border">
    {loading ? (
      <p className="px-4 py-8 text-center text-sm text-gray-500">Carregando…</p>
    ) : !items.length ? (
      <p className="px-4 py-8 text-center text-sm text-gray-500">Sem logs HTTP.</p>
    ) : (
      <div>
        <div className="hidden border-b border-gray-100 bg-gray-50/80 px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:grid lg:grid-cols-[minmax(0,0.4fr)_minmax(0,0.12fr)_minmax(0,0.5fr)_minmax(0,0.12fr)_minmax(0,0.1fr)] dark:border-brand-dark-border dark:bg-brand-dark-elevated/40">
          <span>Data</span>
          <span>Método</span>
          <span>Operação</span>
          <span>Status</span>
          <span>ms</span>
        </div>
        {items.map((entry) => (
          <div
            key={entry.id}
            className="border-b border-gray-100 px-4 py-3 text-sm last:border-0 dark:border-brand-dark-border lg:grid lg:grid-cols-[minmax(0,0.4fr)_minmax(0,0.12fr)_minmax(0,0.5fr)_minmax(0,0.12fr)_minmax(0,0.1fr)] lg:items-center lg:gap-2"
          >
            <span className="text-xs text-gray-500">{formatDate(entry.created_at ?? entry.createdAt)}</span>
            <span className="font-mono text-xs">{entry.http_method ?? entry.httpMethod}</span>
            <span className="truncate font-mono text-xs">{entry.operation}</span>
            <span className="text-xs">{entry.http_status_code ?? entry.httpStatusCode ?? "—"}</span>
            <span className="text-xs tabular-nums">{entry.duration_ms ?? entry.durationMs ?? "—"}</span>
            {(entry.request_body_preview || entry.response_body_preview) && (
              <p className="mt-2 col-span-full truncate text-xs text-gray-400 lg:col-span-5">
                {entry.request_body_preview ?? entry.requestBodyPreview}
                {entry.response_body_preview ?? entry.responseBodyPreview
                  ? ` → ${entry.response_body_preview ?? entry.responseBodyPreview}`
                  : ""}
              </p>
            )}
          </div>
        ))}
      </div>
    )}
    <div className="border-t border-gray-100 px-2 py-2 dark:border-brand-dark-border">
      <GridPagination
        total={total}
        page={page}
        rowsPerPage={pageSize}
        onPageChange={onPageChange}
        onRowsPerPageChange={() => {}}
        currentPageCount={items.length}
        itemLabel="logs"
      />
    </div>
  </div>
);

export const BaasProviderConsolePage = () => {
  const { selectedInstance } = useBaasInstance();
  const { isValutConfigAdmin } = useBaasPlatformCreateAccess();
  const { addToast } = useToast();
  const isLoading = useBaasPageLoading(selectedInstance?.id);
  const [activeTab, setActiveTab] = useState("config");
  const [config, setConfig] = useState(null);
  const [pixLimit, setPixLimit] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [spiDraft, setSpiDraft] = useState("");
  const [spiSaving, setSpiSaving] = useState(false);
  const [ruleDrafts, setRuleDrafts] = useState({});
  const [rulesSaving, setRulesSaving] = useState(false);
  const [webhookItems, setWebhookItems] = useState([]);
  const [webhookTotal, setWebhookTotal] = useState(0);
  const [webhookPage, setWebhookPage] = useState(1);
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [selectedWebhookId, setSelectedWebhookId] = useState(null);
  const [webhookDetail, setWebhookDetail] = useState(null);
  const [webhookDetailLoading, setWebhookDetailLoading] = useState(false);
  const [httpItems, setHttpItems] = useState([]);
  const [httpTotal, setHttpTotal] = useState(0);
  const [httpPage, setHttpPage] = useState(1);
  const [httpLoading, setHttpLoading] = useState(false);

  const pageSize = 20;
  const canView =
    isValutConfigAdmin && selectedInstance?.allowGlobalAccess === true;

  const loadConfig = useCallback(async () => {
    if (!selectedInstance || !canView) return;
    setConfigLoading(true);
    try {
      const [cfg, limits] = await Promise.all([
        fetchBaasProviderPaySmartConfig(selectedInstance),
        fetchBaasProviderPixLimit(selectedInstance),
      ]);
      setConfig(cfg);
      setPixLimit(limits);
      const spi =
        limits?.spi_max_per_operation_amount ?? limits?.spiMaxPerOperationAmount ?? "";
      setSpiDraft(spi ? String(spi) : "");
      const drafts = {};
      const rules = limits?.outbound_rules ?? limits?.outboundRules ?? [];
      rules.forEach((rule) => {
        const key = `${rule.limit_type ?? rule.limitType}-${rule.period}`;
        drafts[key] = String(rule.per_operation_amount ?? rule.perOperationAmount ?? "");
      });
      setRuleDrafts(drafts);
    } catch (error) {
      setConfig(null);
      setPixLimit(null);
      addToast({
        type: "error",
        title: error?.message || "Falha ao carregar configuração PaySmart.",
      });
    } finally {
      setConfigLoading(false);
    }
  }, [selectedInstance, canView, addToast]);

  const handleSaveSpi = useCallback(async () => {
    if (!selectedInstance) return;
    const amount = Number(spiDraft.replace(/\./g, "").replace(",", "."));
    if (!Number.isFinite(amount) || amount <= 0) {
      addToast({ type: "error", title: "SPI max inválido." });
      return;
    }
    setSpiSaving(true);
    try {
      const updated = await updateBaasProviderPixSpiLimit(selectedInstance, amount);
      setPixLimit(updated);
      setSpiDraft(String(updated.spi_max_per_operation_amount ?? updated.spiMaxPerOperationAmount ?? ""));
      addToast({ type: "success", title: "Limite SPI atualizado." });
    } catch (error) {
      addToast({ type: "error", title: error?.message || "Falha ao salvar SPI." });
    } finally {
      setSpiSaving(false);
    }
  }, [selectedInstance, spiDraft, addToast]);

  const handleSaveRules = useCallback(async () => {
    if (!selectedInstance || !pixLimit) return;
    const rules = (pixLimit.outbound_rules ?? pixLimit.outboundRules ?? []).map((rule) => {
      const key = `${rule.limit_type ?? rule.limitType}-${rule.period}`;
      const raw = ruleDrafts[key] ?? rule.per_operation_amount ?? rule.perOperationAmount;
      const amount = Number(String(raw).replace(/\./g, "").replace(",", "."));
      return {
        limit_type: rule.limit_type ?? rule.limitType,
        period: rule.period,
        period_hour_start: rule.period_hour_start ?? rule.periodHourStart,
        period_hour_end: rule.period_hour_end ?? rule.periodHourEnd,
        per_operation_amount: amount,
        valid_from: rule.valid_from ?? rule.validFrom ?? null,
        valid_until: rule.valid_until ?? rule.validUntil ?? null,
      };
    });
    setRulesSaving(true);
    try {
      const updated = await updateBaasProviderPixOutboundRules(selectedInstance, rules);
      setPixLimit(updated);
      addToast({ type: "success", title: "Janelas de saída atualizadas." });
    } catch (error) {
      addToast({ type: "error", title: error?.message || "Falha ao salvar janelas." });
    } finally {
      setRulesSaving(false);
    }
  }, [selectedInstance, pixLimit, ruleDrafts, addToast]);

  const loadWebhooks = useCallback(async () => {
    if (!selectedInstance || !canView) return;
    setWebhooksLoading(true);
    try {
      const data = await fetchBaasInboundWebhookLogs(selectedInstance, {
        page: webhookPage,
        page_size: pageSize,
      });
      setWebhookItems(Array.isArray(data?.items) ? data.items : []);
      setWebhookTotal(Number(data?.total) || 0);
    } catch (error) {
      setWebhookItems([]);
      setWebhookTotal(0);
      addToast({
        type: "error",
        title: error?.message || "Falha ao carregar webhooks inbound.",
      });
    } finally {
      setWebhooksLoading(false);
    }
  }, [selectedInstance, canView, webhookPage, addToast]);

  const loadHttpLogs = useCallback(async () => {
    if (!selectedInstance || !canView) return;
    setHttpLoading(true);
    try {
      const data = await fetchBaasPartnerHttpLogs(selectedInstance, {
        page: httpPage,
        page_size: pageSize,
      });
      setHttpItems(Array.isArray(data?.items) ? data.items : []);
      setHttpTotal(Number(data?.total) || 0);
    } catch (error) {
      setHttpItems([]);
      setHttpTotal(0);
      addToast({
        type: "error",
        title: error?.message || "Falha ao carregar logs HTTP.",
      });
    } finally {
      setHttpLoading(false);
    }
  }, [selectedInstance, canView, httpPage, addToast]);

  useEffect(() => {
    if (!canView) return;
    if (activeTab === "config") loadConfig();
  }, [activeTab, canView, loadConfig]);

  useEffect(() => {
    if (!canView || activeTab !== "webhooks") return;
    loadWebhooks();
  }, [activeTab, canView, webhookPage, loadWebhooks]);

  useEffect(() => {
    if (!canView || activeTab !== "http") return;
    loadHttpLogs();
  }, [activeTab, canView, httpPage, loadHttpLogs]);

  useEffect(() => {
    if (!selectedWebhookId || !selectedInstance || !canView) {
      setWebhookDetail(null);
      return;
    }
    let cancelled = false;
    setWebhookDetailLoading(true);
    (async () => {
      try {
        const detail = await fetchBaasInboundWebhookLogDetail(selectedInstance, selectedWebhookId);
        if (!cancelled) setWebhookDetail(detail);
      } catch {
        if (!cancelled) setWebhookDetail(null);
      } finally {
        if (!cancelled) setWebhookDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedWebhookId, selectedInstance, canView]);

  useEffect(() => {
    setWebhookPage(1);
    setHttpPage(1);
    setSelectedWebhookId(null);
    setWebhookDetail(null);
  }, [selectedInstance?.id]);

  if (isLoading) {
    return <BaasTablePageSkeleton columns={4} />;
  }

  if (!canView) {
    return (
      <div className="min-w-0 space-y-4">
        <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
          Console provedor PaySmart
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Disponível apenas para administradores Valut em instâncias com acesso global.
        </p>
      </div>
    );
  }

  return (
    <div className="baas-provider-console min-w-0 space-y-5">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
            Console provedor PaySmart
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Configuração, webhooks inbound e logs HTTP do parceiro (somente Valut).
          </p>
        </div>
        <button
          type="button"
          className="btn-outline w-auto shrink-0"
          onClick={() => {
            if (activeTab === "config") loadConfig();
            if (activeTab === "webhooks") loadWebhooks();
            if (activeTab === "http") loadHttpLogs();
          }}
        >
          <TbRefresh className="h-4 w-4" aria-hidden />
          {intl.get("baas.pld.refresh") || "Atualizar"}
        </button>
      </header>

      <div className="flex flex-wrap gap-2" role="tablist">
        {TABS.map((tab) => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                active
                  ? "border-[#e7a73f] bg-[#e7a73f]/15 text-brand-dark dark:text-gray-100"
                  : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-brand-dark-border dark:text-gray-300"
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "config" ? (
        <ConfigPanel
          config={config}
          pixLimit={pixLimit}
          loading={configLoading}
          spiDraft={spiDraft}
          onSpiDraftChange={setSpiDraft}
          onSaveSpi={handleSaveSpi}
          spiSaving={spiSaving}
          ruleDrafts={ruleDrafts}
          onRuleDraftChange={(key, value) =>
            setRuleDrafts((prev) => ({ ...prev, [key]: value }))
          }
          onSaveRules={handleSaveRules}
          rulesSaving={rulesSaving}
        />
      ) : null}
      {activeTab === "webhooks" ? (
        <WebhooksPanel
          items={webhookItems}
          loading={webhooksLoading}
          total={webhookTotal}
          page={webhookPage}
          pageSize={pageSize}
          onPageChange={setWebhookPage}
          onSelect={setSelectedWebhookId}
          selectedId={selectedWebhookId}
          selectedDetail={webhookDetail}
          detailLoading={webhookDetailLoading}
        />
      ) : null}
      {activeTab === "http" ? (
        <HttpLogsPanel
          items={httpItems}
          loading={httpLoading}
          total={httpTotal}
          page={httpPage}
          pageSize={pageSize}
          onPageChange={setHttpPage}
        />
      ) : null}
    </div>
  );
};
